/**
 * 行为对比页的纯逻辑（标签配色 / 占比 / 数值格式 / 强事件名），无 React 依赖，便于 node --test。
 */
import type { BehaviorLabel } from '@/apis/simulation';

/** 总览卡与占比条的展示顺序 = 复核优先级。 */
export const LABEL_ORDER: BehaviorLabel[] = [
  'NEEDS_REVIEW',
  'SIGNIFICANT',
  'SLIGHT',
  'CONSISTENT',
  'INCONCLUSIVE',
];

export const LABEL_META: Record<
  BehaviorLabel,
  { name: string; icon: string; color: string; bg: string; border: string }
> = {
  NEEDS_REVIEW: { name: '需复核', icon: '⚠', color: '#c2410c', bg: '#fff1eb', border: '#ffd2bd' },
  SIGNIFICANT: { name: '显著差异', icon: '▲', color: '#c2410c', bg: '#fff4ec', border: '#ffd9bf' },
  SLIGHT: { name: '轻微差异', icon: '△', color: '#a16207', bg: '#fffbeb', border: '#fde68a' },
  CONSISTENT: { name: '一致', icon: '✓', color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' },
  INCONCLUSIVE: { name: '无法判断', icon: '?', color: '#6b7280', bg: '#f3f4f6', border: '#e5e7eb' },
};

/** 占比条分段颜色（与原型一致：橙红 / 橙 / 黄 / 绿 / 灰）。 */
export const LABEL_BAR_COLOR: Record<BehaviorLabel, string> = {
  NEEDS_REVIEW: '#dc5a2a',
  SIGNIFICANT: '#e8893a',
  SLIGHT: '#c4a02c',
  CONSISTENT: '#4caf63',
  INCONCLUSIVE: '#9ca3af',
};

/** 强事件 code → 中文名；与后端 STRONG_EVENT_NAMES 保持一致，新增时两边同步。 */
export const STRONG_EVENT_NAMES: Record<string, string> = {
  TERMINATION_DIFF: '终止原因不同',
  DURATION_DIFF: '运行时长差超过 5 秒',
  ONE_SIDE_CONTACT: '只有一边碰到',
  STOP_COUNT_DIFF: '停车次数不同',
  JUNCTION_COUNT_DIFF: '路口进没进不同',
  CLOSE_CROSS_05: '近距跨 0.5 米',
};

export const labelName = (label: string): string =>
  LABEL_META[label as BehaviorLabel]?.name ?? label;

/** 占比（0~100，保留 1 位）；分母为 0 返回 0。 */
export const percentOf = (n: number, total: number): number =>
  total > 0 ? Math.round((n / total) * 1000) / 10 : 0;

export const formatPercent = (n: number, total: number): string => `${percentOf(n, total).toFixed(1)}%`;

/** 千分位。 */
export const formatCount = (n: number | null | undefined): string =>
  n == null ? '—' : n.toLocaleString('en-US');

export interface BarSegment {
  label: BehaviorLabel;
  count: number;
  widthPct: number;
}

/**
 * 占比条分段：只含数量 > 0 的标签，宽度之和恰好 100（四舍五入误差并入最大段）。
 * 数量极小的段至少给 0.3% 宽度，否则 0.3% 的「需复核」会渲染成不可见。
 */
export const buildBarSegments = (
  counts: Partial<Record<BehaviorLabel, number>>,
): BarSegment[] => {
  const total = LABEL_ORDER.reduce((s, l) => s + (counts[l] ?? 0), 0);
  if (total <= 0) return [];
  const segs = LABEL_ORDER.filter((l) => (counts[l] ?? 0) > 0).map((label) => {
    const count = counts[label] ?? 0;
    return { label, count, widthPct: Math.max((count / total) * 100, 0.3) };
  });
  const sum = segs.reduce((s, x) => s + x.widthPct, 0);
  const scale = 100 / sum;
  return segs.map((s) => ({ ...s, widthPct: s.widthPct * scale }));
};

const UNIT: Record<string, string> = { pos: 'm', speed: 'm/s', yaw: '°', accel: 'm/s²' };
export const METRIC_NAME: Record<string, string> = {
  pos: '位置差',
  speed: '速度差',
  yaw: '朝向差',
  accel: '加速度差',
};

/** 差值展示：保留 2 位，缺失为 —。 */
export const formatDiff = (metric: string, v: number | null | undefined): string =>
  v == null ? '—' : `${v.toFixed(2)}${UNIT[metric] ?? ''}`;

/** 「较上次」文案：+23 / -5 / 持平。 */
export const formatDelta = (d: number | null | undefined): string => {
  if (d == null) return '—';
  if (d === 0) return '持平';
  return d > 0 ? `+${d}` : `${d}`;
};

export const formatCost = (sec: number | null | undefined): string => {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60);
  return m > 0 ? `${m} 分 ${sec % 60} 秒` : `${sec} 秒`;
};

export const verdictText = (v: boolean | null | undefined): string =>
  v == null ? '—' : v ? '通过' : '不通过';

/** 小折线 SVG 的 points：x 均分、y 按 max 归一（上为大），空曲线返回空串。 */
export const sparklinePoints = (
  curve: number[] | null | undefined,
  width: number,
  height: number,
  pad = 2,
): string => {
  if (!curve || curve.length < 2) return '';
  const max = Math.max(...curve, 1e-9);
  const w = width - pad * 2;
  const h = height - pad * 2;
  return curve
    .map((v, i) => `${(pad + (i / (curve.length - 1)) * w).toFixed(1)},${(pad + h - (Math.max(v, 0) / max) * h).toFixed(1)}`)
    .join(' ');
};

/** 曲线最大值所在下标，供标红峰值点；空曲线返回 -1。 */
export const peakIndex = (curve: number[] | null | undefined): number => {
  if (!curve || curve.length === 0) return -1;
  let idx = 0;
  curve.forEach((v, i) => {
    if (v > curve[idx]) idx = i;
  });
  return idx;
};


// ───────────────────────── 失败窗口 / 中文化 / 他车关系 ─────────────────────────

/** 障碍物类型（取自 mcap 内嵌的 PerceptionObstacle.Type 枚举，不是猜的）。 */
export const OBSTACLE_TYPE_NAMES: Record<number, string> = {
  0: '未知',
  1: '未知可移动物',
  2: '未知静止物',
  3: '行人',
  4: '自行车',
  5: '车辆',
};

export const obstacleTypeName = (t: number | null | undefined): string =>
  t == null ? '—' : OBSTACLE_TYPE_NAMES[t] ?? `类型${t}`;

/** 终止原因（运行时原文）→ 中文。没登记的原样返回，不丢信息。 */
const TERMINATION_REASON_NAMES: Record<string, string> = {
  'Ego too far from historical pose': '自车偏离历史轨迹过远',
};

export const terminationReasonName = (raw: string | null | undefined): string => {
  if (!raw) return '—';
  const key = raw.trim();
  return TERMINATION_REASON_NAMES[key] ?? key;
};

/**
 * 主要差异的展示文案：把后端给的 ``终止原因不同 <基线> / <当前>`` 这类带英文原文的文本，
 * 换成 ``终止原因不同``（原文放悬停提示 ``full``）。其它文案原样。
 */
export const shortDiffText = (text: string): { short: string; full: string } => {
  const m = /^(终止原因不同)\s+(.+)$/.exec(text);
  return m ? { short: m[1], full: text } : { short: text, full: text };
};

export interface FailWindowPoint {
  t: number;
  curV: number | null;
  baseV: number | null;
  dPos: number | null;
  dV: number | null;
}

export interface FailWindowLike {
  evaluator: string;
  msg?: string | null;
  responsibility?: string | null;
  failTime: number;
  beforeS: number;
  afterS: number;
  t: (number | null)[];
  cur: { x: (number | null)[]; y: (number | null)[]; v: (number | null)[] };
  base: { x: (number | null)[]; y: (number | null)[]; v: (number | null)[] };
  diff: { pos: (number | null)[]; v: (number | null)[] };
}

/** 把窗口的并行数组转成逐点记录；各数组长度不一致时以 ``t`` 为准，缺的位置补 null。 */
export const failWindowPoints = (w: FailWindowLike): FailWindowPoint[] =>
  (w.t ?? []).map((t, i) => ({
    t: t as number,
    curV: w.cur?.v?.[i] ?? null,
    baseV: w.base?.v?.[i] ?? null,
    dPos: w.diff?.pos?.[i] ?? null,
    dV: w.diff?.v?.[i] ?? null,
  }));

/** 窗口里有数据的点数占比：失败时刻落在数据末尾之外时（真实数据里出现过）后半段是空的，图要提示。 */
export const failWindowCoverage = (w: FailWindowLike): { have: number; total: number } => {
  const pts = failWindowPoints(w);
  return { have: pts.filter((p) => p.curV != null || p.baseV != null).length, total: pts.length };
};

/** 评估器失败窗口的标题：评估器名 + 失败时刻（相对自车首帧的秒）。 */
export const failWindowTitle = (w: FailWindowLike): string =>
  `${w.evaluator} · 第 ${w.failTime.toFixed(1)} 秒${w.responsibility ? ` · 责任方 ${w.responsibility}` : ''}`;

export interface EncounterRow {
  id: number;
  typeName: string;
  minBase: number | null;
  minCur: number | null;
  minDiff: number | null;
  closeDurBase: number;
  closeDurCur: number;
  onlyOneSide: 'base' | 'cur' | null;
  contactBase: boolean;
  contactCur: boolean;
  /** 位于自车哪一侧（左 / 右）：两侧都有时给出变化，如 ``左 → 右`` */
  side: string | null;
  /** 先过 / 后过：``自车先过`` / ``他车先过`` / ``未交汇``，两侧不同时给出变化 */
  passOrder: string | null;
  changed: boolean;
}

const SIDE_NAMES: Record<string, string> = { left: '左', right: '右' };
const ORDER_NAMES: Record<string, string> = {
  ego_passes: '自车先过',
  obstacle_passes: '他车先过',
  no_cross: '未交汇',
};

const both = (a: string | undefined, b: string | undefined, names: Record<string, string>): string | null => {
  if (a == null && b == null) return null;
  const x = a == null ? '—' : names[a] ?? a;
  const y = b == null ? '—' : names[b] ?? b;
  return x === y ? x : `${x} → ${y}`;
};

/** 把 ``facts.interaction`` + ``detail.movement`` 整理成「他车关系」表格行；按最近距离差的绝对值降序。 */
export const buildEncounters = (
  facts: { interaction?: { obstacles?: Record<string, unknown>[] } } | null | undefined,
  movement: { detour_side?: Record<string, unknown>[]; pass_order?: Record<string, unknown>[] } | null | undefined,
): EncounterRow[] => {
  const obstacles = (facts?.interaction?.obstacles ?? []) as Record<string, any>[];
  const sideById = new Map<number, Record<string, any>>(
    ((movement?.detour_side ?? []) as Record<string, any>[]).map((x) => [x.id, x]),
  );
  const orderById = new Map<number, Record<string, any>>(
    ((movement?.pass_order ?? []) as Record<string, any>[]).map((x) => [x.id, x]),
  );
  return obstacles
    .map((o) => {
      const sd = sideById.get(o.id);
      const po = orderById.get(o.id);
      const onlyOneSide: 'base' | 'cur' | null = o.only_base ? 'base' : o.only_cur ? 'cur' : null;
      const minBase = (o.min_base ?? null) as number | null;
      const minCur = (o.min_cur ?? null) as number | null;
      const side = sd ? both(sd.base, sd.cur, SIDE_NAMES) : null;
      const passOrder = po ? both(po.base, po.cur, ORDER_NAMES) : null;
      return {
        id: o.id as number,
        typeName: obstacleTypeName(o.type),
        minBase,
        minCur,
        minDiff: (o.min_diff ?? null) as number | null,
        closeDurBase: (o.close_dur_base ?? 0) as number,
        closeDurCur: (o.close_dur_cur ?? 0) as number,
        onlyOneSide,
        contactBase: minBase != null && minBase <= 0,
        contactCur: minCur != null && minCur <= 0,
        side,
        passOrder,
        changed: !!onlyOneSide || (sd ? !sd.same : false) || (po ? !po.same : false)
          || (o.min_diff != null && Math.abs(o.min_diff) >= 0.5),
      };
    })
    .sort((a, b) => Math.abs(b.minDiff ?? 0) - Math.abs(a.minDiff ?? 0));
};


/**
 * 失败窗口里某一侧的数据在哪个相对时刻之后断掉（该侧运行已终止）。窗口末尾全是 null 才算「终止」；
 * 中间零星的 null 不算（那是网格对不上，不是终止）。返回断点之前最后一个有数据的相对时刻，没有断点返回 null。
 *
 * **网格取整容差**：窗口按 0.1 秒网格取点，真实终止时刻（如 6.19 秒）与失败事件同一时刻时，会落在
 * 网格 -0.0x 附近、被归到 -0.1 那一格。所以「最后有数据的网格点」在失败时刻前一格以内时，视为**与失败时刻同时**
 * （返回 0），免得用户误以为「终止前 0.1 秒就没数据了」。
 */
export const endedAfter = (
  w: Pick<FailWindowLike, 't'>,
  series: (number | null)[],
  gridStep = 0.1,
): number | null => {
  const t = w.t ?? [];
  let last = -1;
  for (let i = 0; i < series.length; i += 1) if (series[i] != null) last = i;
  if (last < 0 || last === series.length - 1) return null; // 全空（没数据，不是「终止」）或一直到最后都有数据
  const at = t[last];
  if (at == null) return null;
  return at < 0 && at >= -gridStep - 1e-9 ? 0 : at;
};

/** 他车关系排序：默认按「两侧最近距离里更近的那个」升序——近的才是真正需要关注的障碍物。 */
export const sortEncountersByProximity = (rows: EncounterRow[]): EncounterRow[] =>
  [...rows].sort((a, b) => nearest(a) - nearest(b));

const nearest = (r: EncounterRow): number => {
  const v = [r.minBase, r.minCur].filter((x): x is number => x != null);
  return v.length ? Math.min(...v) : Number.POSITIVE_INFINITY;
};

/**
 * 他车关系里「距离差」是否主要由自车位置差造成：所有障碍物的距离差几乎相同（离散很小）而自车位置差很大，
 * 说明不是他车行为变了，而是自车本身偏了。返回提示文案，不满足返回 null。
 */
export const positionShiftHint = (rows: EncounterRow[], posMax: number | null | undefined): string | null => {
  const diffs = rows.map((r) => r.minDiff).filter((x): x is number => x != null);
  if (diffs.length < 3 || posMax == null || posMax < 5) return null;
  const mean = diffs.reduce((a, b) => a + b, 0) / diffs.length;
  const spread = Math.max(...diffs) - Math.min(...diffs);
  const similar = Math.abs(mean) >= 5 && spread <= Math.abs(mean) * 0.15;
  return similar
    ? `两侧自车位置差异较大（最大 ${posMax.toFixed(1)} 米），下列距离差值主要反映自车位置的差异，不代表他车行为变化。`
    : null;
};

/**
 * 发起前的耗时预估（分钟）：按抽样里「两侧指纹就绪」的比例折算。
 *
 * 已就绪场景只需读指纹比差异（约 2 秒），缺指纹的要先读录制文件补算：本地实测约 13 秒/场景，
 * 测试集群实测约 3.3 秒/场景，这里取中间值 8 秒，再按分片并发折算总时长。
 * 返回 ``null`` 表示场景总数未知，给不出数字。
 */
export const estimateMinutes = (
  ready: number,
  sampled: number,
  commonSceneNum: number,
  shardSize = 50,
  maxParallel = 20,
): number | null => {
  if (sampled <= 0 || commonSceneNum <= 0) return null;
  const readyRatio = ready / sampled;
  const perScene = readyRatio * 2 + (1 - readyRatio) * 8; // 秒
  const parallel = Math.max(1, Math.min(Math.ceil(commonSceneNum / shardSize), maxParallel));
  const total = (commonSceneNum * perScene) / parallel / 60;
  return Math.max(1, Math.round(total / 5) * 5); // 取整到 5 分钟，避免假精确
};
